import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../../button";
import { Input } from "../../input";
import { Label } from "../../label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../select";

export interface FormQuestionnaireContentProps {
  formTitle: string;
  onFormSubmit?: (formTitle: string, responses: Record<string, string | boolean>) => void;
  onSendToPatient?: () => void;
}

export function FormQuestionnaireContent({ formTitle, onFormSubmit, onSendToPatient }: FormQuestionnaireContentProps) {
  const [responses, setResponses] = useState<Record<string, string | boolean>>({});

  const mockQuestions = [
    {
      id: "q1",
      type: "text",
      label: "Full Name",
      placeholder: "Enter your full name",
    },
    {
      id: "q2",
      type: "email",
      label: "Email Address",
      placeholder: "Enter your email",
    },
    {
      id: "q3",
      type: "select",
      label: "Preferred Contact Method",
      options: ["Email", "Phone", "Text Message", "Portal Message"],
    },
    {
      id: "q4",
      type: "checkbox",
      label: "I have reviewed and agree to the terms and conditions",
    },
    {
      id: "q5",
      type: "checkbox",
      label: "I would like to receive appointment reminders",
    },
    {
      id: "q6",
      type: "text",
      label: "Emergency Contact Name",
      placeholder: "Enter emergency contact name",
    },
    {
      id: "q7",
      type: "text",
      label: "Emergency Contact Phone",
      placeholder: "Enter emergency contact phone",
    },
  ];

  const handleInputChange = (questionId: string, value: string | boolean) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSendToPatient = () => {
    console.log("Sending form to patient:", formTitle);

    // Show success toast
    toast(`"${formTitle}" sent to patient`, {
      description: "The form has been sent successfully",
    });

    if (onSendToPatient) {
      onSendToPatient();
    }
  };

  const handleSubmitForm = () => {
    console.log("Submitting form:", formTitle, responses);
    if (onFormSubmit) {
      onFormSubmit(formTitle, responses);
    }
  };

  return (
    <div className="flex flex-col gap-6 h-full">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">{formTitle}</h3>
        <Button variant="outline" onClick={handleSendToPatient}>
          Send to patient
        </Button>
      </div>

      <div className="flex flex-col gap-4 overflow-auto p-1">
        {mockQuestions.map((question) => (
          <div key={question.id} className="space-y-2">
            {question.type !== "checkbox" && (
              <Label htmlFor={question.id} className="text-sm font-medium">
                {question.label}
              </Label>
            )}

            {question.type === "text" || question.type === "email" ? (
              <Input
                id={question.id}
                type={question.type}
                placeholder={question.placeholder}
                value={(responses[question.id] as string) || ""}
                onChange={(e) => handleInputChange(question.id, e.target.value)}
                className="w-full"
              />
            ) : question.type === "select" ? (
              <Select
                value={(responses[question.id] as string) || ""}
                onValueChange={(value) => handleInputChange(question.id, value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  {question.options?.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : question.type === "checkbox" ? (
              <div className="flex items-center space-x-2">
                <input
                  id={question.id}
                  type="checkbox"
                  checked={(responses[question.id] as boolean) || false}
                  onChange={(e) => handleInputChange(question.id, e.target.checked)}
                  className="rounded border border-input"
                />
                <Label htmlFor={question.id} className="text-sm">
                  {question.label}
                </Label>
              </div>
            ) : null}
          </div>
        ))}

        {/* Submit Form Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={handleSubmitForm} className="w-full md:w-auto">
            Submit Form
          </Button>
        </div>
      </div>
    </div>
  );
}
